import Checkbox from "@mui/material/Checkbox";
import Dialog, { DialogProps } from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import {
  useCreateTranslation,
  useDeleteTranslation,
  usePostTranslations,
} from "apollo/hooks/mutations";
import { QueryProgress } from "core/types";
import { Banner } from "ds/ui";
import RSButton from "ds_legacy/components/RSButton";
import {
  SelectInput,
  optionsToSelectOptions,
} from "ds_legacy/components/SelectInput";
import { border, margin, padding } from "ds_legacy/materials/metrics";
import { Body, Caption, Subheading } from "ds_legacy/materials/typography";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { fromJS } from "immutable";
import React, { useState } from "react";
import dbData from "./fakeData/db.json";
import { InputField } from "./fields";
import { useForm } from "./hooks";
import { locales } from "./index";
import {
  TRANSLATION_ACTIONS,
  useTranslationsDispatch,
} from "./translationsHandler";
import {
  EmailRepositoryType,
  LocalesType,
  RepositoryType,
  SuggestionNoTrigger,
  TranslationType,
} from "./types";
import {
  indexOfSeparator,
  isKeyValid,
  isTranslationValid,
  lastIndexOfSeparator,
  translationSuggestions,
} from "./utils";

export function Modal({
  children,
  fullWidth,
  isConfirmDisabled = false,
  isOpen,
  maxWidth = "lg",
  onConfirm,
  queryProgress,
  setOpen,
  title,
}: Pick<DialogProps, "fullWidth" | "maxWidth"> & {
  children: React.ReactNode;
  isConfirmDisabled?: boolean;
  isOpen: boolean;
  onConfirm?: (...args: Array<ToType>) => void;
  queryProgress?: QueryProgress;
  setOpen: (o: boolean) => void;
  title: React.ReactNode | string;
}) {
  return isOpen ? (
    <Dialog
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{children}</DialogContent>
      <DialogActions>
        <RSButton
          color="primary"
          id="back"
          loading="na"
          variant="outlined"
          onClick={() => setOpen(false)}
        >
          Back
        </RSButton>
        {onConfirm && (
          <RSButton
            color="primary"
            disabled={isConfirmDisabled}
            id="confirm"
            loading={queryProgress || "na"}
            variant="contained"
            onClick={() => onConfirm()}
          >
            Confirm
          </RSButton>
        )}
      </DialogActions>
    </Dialog>
  ) : null;
}

export const RemoveEntryModal = ({
  isOpen,
  repo,
  setOpen,
  value,
  valueKey,
}: {
  isOpen: boolean;
  repo: RepositoryType;
  setOpen: (o: boolean) => void;
  value?: TranslationType;
  valueKey: string;
}) => {
  const toast = useToast();
  const [deleteTranslation, queryProgress] = useDeleteTranslation(repo);
  const dispatch = useTranslationsDispatch();

  return (
    <Modal
      fullWidth
      isOpen={isOpen}
      queryProgress={queryProgress}
      setOpen={setOpen}
      title="Remove translation"
      onConfirm={() => {
        deleteTranslation({ variables: { id: valueKey } })
          .then(() => {
            dispatch({
              payload: { id: valueKey },
              type: TRANSLATION_ACTIONS.DELETE,
            });
          })
          .catch(() => {
            toast({
              color: "danger",
              message: "An error occured, please try again and/or refresh",
            });
          });
      }}
    >
      <Body>
        Youre about to remove: <b>{valueKey}</b>
      </Body>
      <br />
      {value &&
        Object.keys(locales).map((key) => (
          <span key={key}>
            <Body>
              <b>{locales[key]}:</b>&nbsp;
              {(value.locales as { [key: string]: string })[key]}
            </Body>
            <br />
          </span>
        ))}
    </Modal>
  );
};

// checks that translations are not overwriting folders and vice versa
export const checkIsOverwriting = (
  key: string | number,
  originKey: string | undefined,
  translations: AnyObject,
) => {
  const originKeyMap = fromJS(translations);

  // if no originKey then we are at the root
  const item = originKey
    ? originKeyMap?.getIn(originKey.split("."))
    : originKeyMap;

  const jsItem = item?.toJS() ?? {};

  return key in jsItem;
};

export const AddEntryModal = ({
  isOpen,
  originKey,
  repo,
  setOpen,
  translations,
}: {
  originKey?: string;
  repo: RepositoryType;
  setOpen: (o: boolean) => void;
  isOpen: boolean;
  translations: AnyObject;
}) => {
  const dispatch = useTranslationsDispatch();
  const toast = useToast();
  const [createTranslation, queryProgress] = useCreateTranslation(repo);
  const { formState, setFormValue, submit } = useForm(
    {
      description: "",
      key: {
        validate: (v) => {
          const key = String(v);
          const isOverwriting = checkIsOverwriting(
            key,
            originKey,
            translations,
          );
          if (isOverwriting) {
            return "specified translation key already exists";
          }
          return isKeyValid(v);
        },
        value: "",
      },
      nest: false,
      nestKey: {
        validate: (v, form) => {
          if (form?.nest?.value) {
            const nestKey = String(v);
            const isOverwriting = checkIsOverwriting(
              nestKey,
              originKey,
              translations,
            );
            if (isOverwriting) {
              return "specified folder is already a translation";
            }
            return isKeyValid(v);
          }
          return null;
        },
        value: "",
      },
      tags: [],
      ...Object.keys(locales).reduce(
        (form, key) => ({
          ...form,
          [key]: { validate: isTranslationValid(key), value: "" },
        }),
        {},
      ),
    },
    () => {
      const values: LocalesType = Object.keys(locales).reduce(
        (v, key) => ({
          ...v,
          [key]: formState[key].value || "",
        }),
        {} as LocalesType,
      );
      const description = formState.description.value;
      const tags = formState.tags.value;
      const id = [originKey, formState.nestKey.value, formState.key.value]
        .filter((p) => p != null && p !== "." && p)
        .join(".");

      const payload: Partial<TranslationType> = {
        description: description ? String(description) : null,
        id,
        locales: values,
        // #TODO: fix casting
        tags: tags as unknown as number[],
      };

      createTranslation(payload)
        .then(() => {
          dispatch({
            payload,
            type: TRANSLATION_ACTIONS.ADD,
          });
          setOpen(false);
          toast({
            color: "primary",
            message: `${id} has been added, don't forget to export !`,
          });
        })
        .catch((e) => {
          const errorDefault = "An error occurred, please try again";
          try {
            const test = JSON.stringify(e);
            if (test.includes("duplicate key value violates unique constraint"))
              toast({
                color: "danger",
                message: `The translation "${id}" already exists`,
              });
            else toast({ color: "danger", message: errorDefault });
          } catch (_) {
            toast({ color: "danger", message: errorDefault });
          }
        });
    },
  );

  if (!isOpen) return null;

  return (
    <Modal
      fullWidth
      isOpen={isOpen}
      queryProgress={queryProgress}
      setOpen={setOpen}
      title={
        <>
          Add a translation in <b>{originKey ?? "root"}</b>
        </>
      }
      onConfirm={submit}
    >
      <Banner
        color="primary"
        heading="Please fill the required fields in this modal to create the translation"
        message={`Note that adding a translation inside a folder, will
        first create the folder and then add the created
        translation inside the folder`}
      />
      <div style={{ margin: margin(0, 2) }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formState.nest.value as boolean}
              onChange={(e) => setFormValue("nest", e.target.checked)}
            />
          }
          label="Add inside a folder"
        />
        {formState.nest.value && (
          <InputField
            key="Folder name"
            error={formState.nestKey.error}
            isRequired
            label="Folder name"
            placeholder="Examples: 'careproviderApp', 'acpApp', 'auctionResponse'..."
            rows="1"
            setValue={(v) => setFormValue("nestKey", v)}
            tabIndex={0}
            value={formState.nestKey.value}
          />
        )}
        <InputField
          key="Name"
          error={formState.key.error}
          hasAutoFocus
          isRequired
          label="Name"
          placeholder="Examples: 'title', 'confirmButton', 'requestCreated', 'explanation'.."
          rows="1"
          setValue={(v) => setFormValue("key", v)}
          tabIndex={1}
          value={formState.key.value}
        />
        <InputField
          key="Description"
          error={formState.description.error}
          label="Description"
          placeholder="Example: 'Explanation text of confirmation modal when editing search'"
          rows="1"
          setValue={(value) => setFormValue("description", value)}
          tabIndex={2}
          value={formState.description.value}
        />
        {Object.keys(locales).map((key, idx) => (
          <InputField
            key={locales[key]}
            error={formState[key].error}
            id={key}
            isRequired={key === "en"}
            label={locales[key]}
            setTags={(tags) => setFormValue("tags", tags)}
            setValue={(value) => setFormValue(key, value)}
            suggestions={translationSuggestions}
            tabIndex={idx + 2}
            tags={
              Array.isArray(formState.tags.value) ? formState.tags.value : []
            }
            value={formState[key].value}
          />
        ))}
      </div>
    </Modal>
  );
};

enum InputMode {
  dictionary = "dictionary",
  free = "free",
}

export function ActivableBox({
  activate,
  children,
  isActive,
}: {
  activate: () => void;
  children: React.ReactNode;
  isActive: boolean;
}) {
  return (
    <div
      style={{
        opacity: isActive ? 1 : 0.25,
        userSelect: isActive ? undefined : "none",
      }}
      onClick={() => activate()}
    >
      {children}
    </div>
  );
}

function RequirementInput({
  dictionary,
  req,
  update,
  value,
}: {
  dictionary: string[] | undefined;
  req: string;
  update: (updateArgs: { key: string; value: ToType }) => void;
  value: string;
}) {
  const [inputMode, setInputMode] = useState<InputMode>(
    dictionary != null ? InputMode.dictionary : InputMode.free,
  );

  if (!dictionary || !(dictionary.length > 0))
    return (
      <InputField
        error={null}
        label={req}
        rows="1"
        setValue={(v) => update({ key: req, value: v })}
        value={value}
      />
    );

  return (
    <>
      <br />
      <div
        style={{
          borderLeft: border({ color: "lightgray", width: 2 }),
          padding: padding(0, 0, 0, 1),
        }}
      >
        <Subheading margin="0">{req}:</Subheading>
        {Object.values(InputMode).map((mode) => {
          const active = inputMode === mode;
          const activate = () => setInputMode(mode);

          switch (mode) {
            case InputMode.dictionary:
              return (
                <ActivableBox key={mode} activate={activate} isActive={active}>
                  <SelectInput
                    elementName="dictionary"
                    label="Dictionary"
                    options={optionsToSelectOptions(dictionary)}
                    value={active && value ? value : null}
                    onChange={(value) => update({ key: req, value })}
                  />
                </ActivableBox>
              );
            case InputMode.free:
            default:
              return (
                <ActivableBox key={mode} activate={activate} isActive={active}>
                  <InputField
                    error={null}
                    label="Override dictionary value"
                    rows="1"
                    setValue={(v) => update({ key: req, value: v })}
                    value={active ? value : ""}
                  />
                </ActivableBox>
              );
          }
        })}
      </div>
    </>
  );
}

export function AutocompleteModal({
  isOpen,
  setOpen,
  setVal,
  stringStart,
  suggestion,
  val,
}: {
  isOpen: boolean;
  setOpen: (o: boolean) => void;
  setVal: (v: string) => void;
  stringStart: number;
  suggestion: SuggestionNoTrigger;
  val: string;
}) {
  const [formState, setFormState] = useState(
    suggestion.requirements.reduce(
      (acc, { name }) => ({ ...acc, [name]: "" }),
      {} as AnyObject,
    ),
  );
  const update = ({ key, value }: { key: string; value: ToType }) =>
    setFormState((state) => ({ ...state, [key]: value }));

  if (!isOpen) return null;

  return (
    <Modal
      fullWidth
      isOpen={isOpen}
      maxWidth="tablet"
      setOpen={setOpen}
      title="Macro auto-complete"
      onConfirm={() => {
        const startAnchor = lastIndexOfSeparator(val.slice(0, stringStart));
        const endAnchor = indexOfSeparator(val, stringStart);
        setVal(
          `${val.slice(
            0,
            startAnchor === -1 ? 0 : startAnchor + 1,
          )}${suggestion.complete(Object.values(formState))}${
            endAnchor === -1 ? "" : val.slice(endAnchor, val.length)
          }`,
        );
      }}
    >
      <Subheading style={{ margin: margin(0) }}>
        {`${suggestion.description
          .charAt(0)
          .toUpperCase()}${suggestion.description.slice(1)}`}
      </Subheading>
      {suggestion.requirements.map(({ dictionary, name }) => (
        <RequirementInput
          key={name}
          dictionary={dictionary}
          req={name}
          update={update}
          value={formState[name]}
        />
      ))}
    </Modal>
  );
}

export function HelpModal({
  isOpen,
  setOpen,
}: {
  isOpen: boolean;
  setOpen: (o: boolean) => void;
}) {
  const intro =
    "The main purpose of this tool is to ease the process of updating and adding translations of our applications, here's how it's meant to be used:";
  const leftPanel =
    ".1 Click on a translation in the scrollable panel on your left";
  const rightPanel =
    ".2 Change up the translations as you wish on the panel on your right";
  const translationsGuidelines =
    "/!\\ Please note that while you can 'ignore' any french or german translations, the english should always be filled with the correct data (macros...)";
  const saving = '.3 Click "OK" in order to save the translation';
  const savingWarning =
    "/!\\ Keep in mind that saving does not update anything in the applications";
  const eXport =
    '.4 Click on "EXPORT" on the bottom right of your screen to syncronize your changes with the applications';
  const exportWarning =
    "/!\\ This is a time consuming (~2 mins) operation make sure that everything you wished to update has been updated";
  const deployment =
    "When the export ended, you will need to wait around 20 minutes to have your changes appear live on preprod";

  return (
    <Modal fullWidth isOpen={isOpen} setOpen={setOpen} title="Help page">
      <Subheading>{intro}</Subheading>
      <br />
      <Subheading margin={margin(1, 4)}>
        {leftPanel}
        <br />
        {rightPanel}
        <br />
        <Caption style={{ margin: margin(0, 1) }}>
          {translationsGuidelines}
        </Caption>
        <br />
        {saving}
        <br />
        <Caption style={{ margin: margin(0, 1) }}>{savingWarning}</Caption>
        <br />
        {eXport}
        <br />
        <Caption style={{ margin: margin(0, 1) }}>{exportWarning}</Caption>
        <br />
      </Subheading>
      <br />
      <Subheading>{deployment}</Subheading>
    </Modal>
  );
}

export function ExportFromSourceModal({
  isOpen,
  repo,
  setOpen,
}: {
  isOpen: boolean;
  repo: RepositoryType | EmailRepositoryType;
  setOpen: (o: boolean) => void;
}) {
  const toast = useToast();
  const [postTranslations, queryProgress] = usePostTranslations(repo);

  return (
    <Modal
      isOpen={isOpen}
      queryProgress={queryProgress}
      setOpen={setOpen}
      title="Update from codebase"
      onConfirm={() => {
        postTranslations({ variables: { input: dbData } })
          .then(() => window.location.reload())
          .catch(() =>
            toast({ color: "danger", message: "Failed, try again.." }),
          );
      }}
    >
      <Subheading>
        For this to work properly, you need to first run:
        <br />
        <code
          style={{
            backgroundColor: "darkgrey",
            border: border({ color: "black" }),
            borderRadius: "5px",
            color: "white",
            padding: padding(1),
          }}
        >
          yarn translations:DB
        </code>
        <br />
        Then click confirm in this modal to replace the backend data with the
        codebase version
      </Subheading>
    </Modal>
  );
}
